import {
  REVIEW_STATES,
  TERMINAL_REVIEW_STATES
} from "./smartUploadLineRequirements";

// Front-end mirror of functions/utils/smart-upload/csvLineAttention.js --
// which migrated CSV lines need a person to look at them (ticket #393).
// functions/__tests__/utils/smart-upload/unit/test_csvLineAttentionParity.js
// reads this file's text and fails if the reasons or the exported rules
// drift from the server module, so the reason values stay literals.

export const ATTENTION_REASONS = Object.freeze({
  possibleDuplicate: "possible_duplicate",
  vendorMatchUnconfirmed: "vendor_match_unconfirmed",
  noVendor: "no_vendor",
  vendorFactorMissing: "vendor_factor_missing",
  vendorLookupFailed: "vendor_lookup_failed",
  bomNewSupplier: "bom_new_supplier",
  bomSupplierLookupFailed: "bom_supplier_lookup_failed",
  bomSupplierMatchUnconfirmed: "bom_supplier_match_unconfirmed"
});

export const GROUP_KEY_FIELDS = Object.freeze({
  vendor: "vendor_group_key",
  service: "service_type_group_key",
  usage: "usage_type_group_key"
});

const TRAILING_COUNTRY_TOKEN = /\s+(usa|us|united states( of america)?)$/;

// Same normalization as the server's normalizeMatchKey.js.
const normalizeMatchKey = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }
  const normalized = value
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(TRAILING_COUNTRY_TOKEN, "");
  return normalized || null;
};

const hasText = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value.trim() !== "undefined";

const hasReviewerVendorChoice = (line) =>
  !!line.vendor_relationship_id_final ||
  hasText(line.vendor_name_final) ||
  !!line.vendor_match_accepted ||
  !!line.force_new_vendor;

const csvVendorName = (line) =>
  line.vendor_name_ai || line.plaid_enrichment?.vendor?.name || null;

const isUnconfirmedFuzzyMatch = (line) =>
  line.vendor_match_ai?.match_source === "fuzzy" &&
  !line.vendor_match_ai?.isConfirmed &&
  !hasReviewerVendorChoice(line);

const hasNoVendor = (line) =>
  !hasText(csvVendorName(line)) && !hasReviewerVendorChoice(line);

const hasMissingFactor = (line) =>
  (line.commit_error?.reason === ATTENTION_REASONS.vendorFactorMissing ||
    !!line.vendor_factor_zero_ai) &&
  !line.vendor_relationship_id_final &&
  !hasText(line.vendor_name_final);

// The draft couldn't look the vendor up (Plaid or matching failed), so
// nothing was pre-matched; any vendor the reviewer picks or types clears it.
const hasFailedLookup = (line) =>
  !!line.vendor_match_failed && !hasReviewerVendorChoice(line);

const TRANSACTIONS_RULES = [
  {
    reason: ATTENTION_REASONS.vendorMatchUnconfirmed,
    applies: isUnconfirmedFuzzyMatch
  },
  { reason: ATTENTION_REASONS.noVendor, applies: hasNoVendor },
  { reason: ATTENTION_REASONS.vendorFactorMissing, applies: hasMissingFactor },
  { reason: ATTENTION_REASONS.vendorLookupFailed, applies: hasFailedLookup }
];

// A reviewer settles a new-supplier line either by picking an existing
// vendor instead, or by explicitly acknowledging the creation.
const hasBomSupplierChoice = (line) =>
  !!line.vendor_relationship_id_final || !!line.supplier_creation_acknowledged;

const needsNewSupplierAck = (line) =>
  line.supplier_match?.action === "create" && !hasBomSupplierChoice(line);

const hasFailedSupplierLookup = (line) =>
  line.supplier_match?.action === "lookup_failed" && !hasBomSupplierChoice(line);

// The BOM equivalent of isUnconfirmedFuzzyMatch above, for the "match" action
// lookupBomSupplier returns on an unconfirmed (including cross-company) hit.
const hasUnconfirmedSupplierMatch = (line) =>
  line.supplier_match?.action === "match" &&
  !!line.supplier_match?.needsVendorLink &&
  !hasBomSupplierChoice(line);

const BOM_RULES = [
  { reason: ATTENTION_REASONS.bomNewSupplier, applies: needsNewSupplierAck },
  { reason: ATTENTION_REASONS.bomSupplierLookupFailed, applies: hasFailedSupplierLookup },
  { reason: ATTENTION_REASONS.bomSupplierMatchUnconfirmed, applies: hasUnconfirmedSupplierMatch }
];

// Same shape as the server's RULES_BY_FAMILY -- every family's rules live in
// this map, transactions_csv included, so attentionReasonsFor needs no
// special-cased family check of its own.
const RULES_BY_FAMILY = Object.freeze({
  transactions_csv: TRANSACTIONS_RULES,
  bom_products_csv: BOM_RULES
});

const isOpenDuplicate = (line) =>
  !!line.duplicate_of && !line.duplicate_resolution;

const isSettled = (line) =>
  TERMINAL_REVIEW_STATES.has(line.review_state) ||
  line.review_state === REVIEW_STATES.committing;

export const attentionReasonsFor = (line = {}) => {
  if (isSettled(line)) {
    return [];
  }
  const familyRules = RULES_BY_FAMILY[line.document_family] || [];
  return [
    ...(isOpenDuplicate(line) ? [ATTENTION_REASONS.possibleDuplicate] : []),
    ...familyRules
      .filter(({ applies }) => applies(line))
      .map(({ reason }) => reason)
  ];
};

export const attentionFieldsFor = (line) => {
  const reasons = attentionReasonsFor(line);
  return { attention_reasons: reasons, needs_attention: reasons.length > 0 };
};

export const needsAttention = (line) => attentionReasonsFor(line).length > 0;

export const isReadyToConfirm = (line = {}) =>
  line.review_state === REVIEW_STATES.pendingReview && !needsAttention(line);

export const vendorGroupKeyFor = (line) => {
  const key = normalizeMatchKey(csvVendorName(line) || "");
  return key ? `vendor:${key}` : "vendor:none";
};

export const serviceTypeGroupKeyFor = (serviceType) => {
  const key = normalizeMatchKey(serviceType || "");
  return key ? `service:${key}` : "service:none";
};

// utilities_csv/office_csv group by usage type (ticket #412) rather than
// vendor or service -- there's no vendor on these lines at all.
export const usageTypeGroupKeyFor = (usageTypeLabel) => {
  const key = normalizeMatchKey(usageTypeLabel || "");
  return key ? `usage:${key}` : "usage:none";
};
